import { useCallback, useEffect, useMemo, useState } from 'react'
import { useAuth } from '../../hooks/useAuth'
import { supabase } from '../../services/supabase'
import { currentLeaveYear } from './constants'
import { useSimpleHqAccess } from './useSimpleHqAccess'
import { formatShortDate, leaveBusinessDays } from './hqUtils'

type LeaveType = {
  id: string
  name: string
  code: string
  allow_half_day: boolean
  allow_negative: boolean
  default_days: number
}

type MyLeaveRow = {
  id: string
  leave_type_id: string
  start_date: string
  end_date: string
  days: number
  is_half_day: boolean
  half_day_period: string | null
  reason: string | null
  status: string
  created_at: string
}

export function LeaveRequestView() {
  const { user } = useAuth()
  const { companyId } = useSimpleHqAccess()
  const [types, setTypes] = useState<LeaveType[]>([])
  const [typeId, setTypeId] = useState('')
  const [start, setStart] = useState('')
  const [end, setEnd] = useState('')
  const [half, setHalf] = useState(false)
  const [halfPeriod, setHalfPeriod] = useState<'am' | 'pm'>('am')
  const [reason, setReason] = useState('')
  const [remaining, setRemaining] = useState<number | null>(null)
  const [blackouts, setBlackouts] = useState<{ start_date: string; end_date: string; name: string }[]>([])
  const [msg, setMsg] = useState<string | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [mine, setMine] = useState<MyLeaveRow[]>([])
  const [editingId, setEditingId] = useState<string | null>(null)

  const selected = types.find((t) => t.id === typeId)
  const days = useMemo(
    () => (start && end ? leaveBusinessDays(start, end, half && !!selected?.allow_half_day) : 0),
    [start, end, half, selected],
  )

  const typeName = useCallback(
    (id: string) => types.find((t) => t.id === id)?.name || 'Leave',
    [types],
  )

  const loadMine = useCallback(async () => {
    if (!user?.id) return
    const { data } = await supabase
      .from('leave_requests')
      .select(
        'id, leave_type_id, start_date, end_date, days, is_half_day, half_day_period, reason, status, created_at',
      )
      .eq('company_id', companyId)
      .eq('user_id', user.id)
      .in('status', ['pending', 'approved', 'denied', 'cancelled'])
      .order('start_date', { ascending: false })
      .limit(40)
    setMine((data as MyLeaveRow[]) || [])
  }, [companyId, user?.id])

  useEffect(() => {
    ;(async () => {
      const [{ data: t }, { data: b }] = await Promise.all([
        supabase
          .from('leave_types')
          .select('id, name, code, allow_half_day, allow_negative, default_days')
          .eq('company_id', companyId)
          .eq('active', true)
          .order('sort_order'),
        supabase
          .from('leave_blackouts')
          .select('start_date, end_date, name')
          .eq('company_id', companyId),
      ])
      setTypes((t as LeaveType[]) || [])
      if (t?.[0]) setTypeId(t[0].id)
      setBlackouts(b || [])
    })()
  }, [companyId])

  useEffect(() => {
    void loadMine()
  }, [loadMine])

  useEffect(() => {
    if (!user?.id || !typeId) {
      setRemaining(null)
      return
    }
    ;(async () => {
      const { data } = await supabase.rpc('leave_remaining_days', {
        p_user_id: user.id,
        p_leave_type_id: typeId,
        p_leave_year: currentLeaveYear(),
      })
      setRemaining(typeof data === 'number' ? data : Number(data))
    })()
  }, [user?.id, typeId])

  const inBlackout = useMemo(() => {
    if (!start || !end) return null
    return blackouts.find((b) => start <= b.end_date && b.start_date <= end) || null
  }, [start, end, blackouts])

  const overBalance =
    remaining != null && selected && !selected.allow_negative && days > remaining

  const resetForm = () => {
    setEditingId(null)
    setStart('')
    setEnd('')
    setHalf(false)
    setHalfPeriod('am')
    setReason('')
  }

  const beginEdit = (row: MyLeaveRow) => {
    setEditingId(row.id)
    setTypeId(row.leave_type_id)
    setStart(row.start_date)
    setEnd(row.end_date)
    setHalf(!!row.is_half_day)
    setHalfPeriod((row.half_day_period as 'am' | 'pm') || 'am')
    setReason(row.reason || '')
    setMsg(null)
    setErr(null)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const notify = async (
    kind: string,
    leaveRequestId: string,
    extra?: Record<string, unknown>,
  ) => {
    const { data, error } = await supabase.functions.invoke('simple-hq-notify', {
      body: { kind, leaveRequestId, ...extra },
    })
    if (error) {
      console.warn('notify', error.message)
      return { emailOk: false, emailError: error.message }
    }
    return data as { emailOk?: boolean; emailError?: string }
  }

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setErr(null)
    setMsg(null)
    if (!user?.id || !typeId || !start || !end || days <= 0) {
      setErr('Pick a type and valid dates.')
      return
    }
    if (overBalance) {
      setErr(`Not enough balance (${remaining} days remaining).`)
      return
    }
    if (inBlackout) {
      setErr(`Dates overlap blackout: ${inBlackout.name}`)
      return
    }
    setBusy(true)
    try {
      if (editingId) {
        const { error } = await supabase
          .from('leave_requests')
          .update({
            leave_type_id: typeId,
            start_date: start,
            end_date: end,
            is_half_day: half && !!selected?.allow_half_day,
            half_day_period: half ? halfPeriod : null,
            days,
            reason: reason || null,
            updated_at: new Date().toISOString(),
          })
          .eq('id', editingId)
          .eq('user_id', user.id)
          .eq('status', 'pending')
        if (error) throw error

        const n = await notify('leave_updated', editingId)
        setMsg(
          n.emailOk === false
            ? 'Request updated, but email notify failed — ask an admin to check Approvals.'
            : 'Request updated. Approvers have been notified.',
        )
        resetForm()
      } else {
        const { data, error } = await supabase
          .from('leave_requests')
          .insert({
            company_id: companyId,
            user_id: user.id,
            leave_type_id: typeId,
            start_date: start,
            end_date: end,
            is_half_day: half && !!selected?.allow_half_day,
            half_day_period: half ? halfPeriod : null,
            days,
            reason: reason || null,
            status: 'pending',
          })
          .select('id')
          .single()
        if (error) throw error

        const n = await notify('leave_requested', data.id)
        setMsg(
          n.emailOk === false
            ? 'Leave saved, but email notify failed — ask an admin to open Approvals.'
            : 'Leave requested. Approvers have been notified.',
        )
        setReason('')
        setStart('')
        setEnd('')
        setHalf(false)
      }

      const { data: rem } = await supabase.rpc('leave_remaining_days', {
        p_user_id: user.id,
        p_leave_type_id: typeId,
        p_leave_year: currentLeaveYear(),
      })
      setRemaining(typeof rem === 'number' ? rem : Number(rem))
      await loadMine()
    } catch (ex: any) {
      setErr(ex.message || 'Failed to submit')
    } finally {
      setBusy(false)
    }
  }

  const cancelRequest = async (row: MyLeaveRow) => {
    if (!user?.id) return
    if (!confirm(`Cancel this ${typeName(row.leave_type_id)} request (${row.start_date} → ${row.end_date})?`)) {
      return
    }
    setBusy(true)
    setErr(null)
    setMsg(null)
    try {
      const { data: prevStatus, error } = await supabase.rpc('cancel_my_leave_request', {
        p_id: row.id,
      })
      if (error) throw error

      const prior = (prevStatus as string) || row.status
      const n = await notify('leave_cancelled', row.id, { previousStatus: prior })
      if (editingId === row.id) resetForm()

      // Restore balance display (approved days no longer count once cancelled)
      if (row.leave_type_id) {
        const { data: rem } = await supabase.rpc('leave_remaining_days', {
          p_user_id: user.id,
          p_leave_type_id: row.leave_type_id,
          p_leave_year: currentLeaveYear(),
        })
        if (typeId === row.leave_type_id || !typeId) {
          setRemaining(typeof rem === 'number' ? rem : Number(rem))
        }
      }

      setMsg(
        n.emailOk === false
          ? prior === 'approved'
            ? 'Cancelled — removed from calendar and balance restored, but admin email failed.'
            : 'Cancelled, but admin email failed.'
          : prior === 'approved'
            ? 'Cancelled. Removed from the team calendar, balance restored, and admins notified.'
            : 'Cancelled. Admins have been notified.',
      )
      await loadMine()
    } catch (ex: any) {
      setErr(ex.message || 'Failed to cancel')
    } finally {
      setBusy(false)
    }
  }

  const pending = mine.filter((r) => r.status === 'pending')
  const upcoming = mine.filter((r) => r.status === 'approved')
  const other = mine.filter((r) => r.status === 'denied' || r.status === 'cancelled').slice(0, 8)

  return (
    <div>
      <p className="hq-eyebrow">Leave</p>
      <h1 className="hq-title">{editingId ? 'Edit leave request' : 'Request leave'}</h1>
      <p className="hq-sub">
        Balance shows before you submit. Weekends are not counted. Manage pending requests below.
      </p>

      <form className="hq-panel max-w-xl" onSubmit={(e) => void submit(e)}>
        {editingId && (
          <div className="hq-ok-box mb-3">
            Editing a pending request.{' '}
            <button type="button" className="underline font-semibold" onClick={resetForm}>
              Cancel edit
            </button>
          </div>
        )}

        <div className="hq-field">
          <label htmlFor="leave-type">Leave type</label>
          <select id="leave-type" value={typeId} onChange={(e) => setTypeId(e.target.value)}>
            {types.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </div>

        {remaining != null && (
          <div className="hq-ok-box">
            Remaining this year: <strong>{remaining}</strong> day(s)
            {days > 0 ? ` · this request: ${days}` : ''}
            {overBalance ? ' · over balance' : ''}
          </div>
        )}

        <div className="hq-row">
          <div className="hq-field">
            <label htmlFor="start">Start</label>
            <input id="start" type="date" value={start} onChange={(e) => setStart(e.target.value)} required />
          </div>
          <div className="hq-field">
            <label htmlFor="end">End</label>
            <input
              id="end"
              type="date"
              value={end}
              min={start || undefined}
              onChange={(e) => setEnd(e.target.value)}
              required
            />
          </div>
        </div>

        {selected?.allow_half_day && start && start === end && (
          <div className="hq-row">
            <label className="flex items-center gap-2 text-sm mb-3">
              <input type="checkbox" checked={half} onChange={(e) => setHalf(e.target.checked)} />
              Half day
            </label>
            {half && (
              <div className="hq-field">
                <label htmlFor="half-period">Period</label>
                <select
                  id="half-period"
                  value={halfPeriod}
                  onChange={(e) => setHalfPeriod(e.target.value as 'am' | 'pm')}
                >
                  <option value="am">Morning</option>
                  <option value="pm">Afternoon</option>
                </select>
              </div>
            )}
          </div>
        )}

        <div className="hq-field">
          <label htmlFor="reason">Note (optional)</label>
          <textarea id="reason" value={reason} onChange={(e) => setReason(e.target.value)} />
        </div>

        {inBlackout && <div className="hq-warn-box">Blackout period: {inBlackout.name}</div>}
        {overBalance && <div className="hq-warn-box">This request exceeds your remaining balance.</div>}
        {err && <div className="hq-warn-box">{err}</div>}
        {msg && <div className="hq-ok-box">{msg}</div>}

        <button type="submit" className="hq-btn hq-btn-primary" disabled={busy || overBalance}>
          {busy ? 'Saving…' : editingId ? 'Save changes' : 'Submit request'}
        </button>
      </form>

      <section className="mt-8">
        <h2 className="hq-title text-xl mb-1">My requests</h2>
        <p className="hq-sub mb-4">Edit or cancel pending leave. Cancel approved future leave if plans change.</p>

        <div className="hq-panel mb-4">
          <p className="hq-eyebrow">Pending ({pending.length})</p>
          {pending.length === 0 ? (
            <p className="hq-empty">No pending requests.</p>
          ) : (
            <ul className="flex flex-col gap-3">
              {pending.map((r) => (
                <li key={r.id} className="flex flex-wrap items-center justify-between gap-2 border-b border-[var(--hq-line)] pb-3 last:border-0">
                  <div>
                    <div className="font-semibold">
                      {typeName(r.leave_type_id)} · {formatShortDate(r.start_date)}–{formatShortDate(r.end_date)}
                    </div>
                    <div className="text-sm text-[var(--hq-muted)]">
                      {r.days} day(s) · <span className="hq-badge hq-badge-pending">Pending</span>
                      {r.reason ? ` · ${r.reason}` : ''}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button type="button" className="hq-btn hq-btn-primary" disabled={busy} onClick={() => beginEdit(r)}>
                      Edit
                    </button>
                    <button type="button" className="hq-btn" disabled={busy} onClick={() => void cancelRequest(r)}>
                      Cancel
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="hq-panel mb-4">
          <p className="hq-eyebrow">Approved ({upcoming.length})</p>
          {upcoming.length === 0 ? (
            <p className="hq-empty">No approved leave on file.</p>
          ) : (
            <ul className="flex flex-col gap-3">
              {upcoming.map((r) => (
                <li key={r.id} className="flex flex-wrap items-center justify-between gap-2 border-b border-[var(--hq-line)] pb-3 last:border-0">
                  <div>
                    <div className="font-semibold">
                      {typeName(r.leave_type_id)} · {formatShortDate(r.start_date)}–{formatShortDate(r.end_date)}
                    </div>
                    <div className="text-sm text-[var(--hq-muted)]">
                      {r.days} day(s) · <span className="hq-badge hq-badge-approved">Approved</span>
                    </div>
                  </div>
                  <button type="button" className="hq-btn" disabled={busy} onClick={() => void cancelRequest(r)}>
                    Cancel leave
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {other.length > 0 && (
          <div className="hq-panel">
            <p className="hq-eyebrow">Recent denied / cancelled</p>
            <ul className="flex flex-col gap-2 text-sm text-[var(--hq-muted)]">
              {other.map((r) => (
                <li key={r.id}>
                  {typeName(r.leave_type_id)} · {formatShortDate(r.start_date)}–{formatShortDate(r.end_date)} ·{' '}
                  <span className={`hq-badge hq-badge-${r.status === 'denied' ? 'denied' : 'cancelled'}`}>{r.status}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>
    </div>
  )
}
